import React from 'react';
import PropTypes from 'prop-types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box } from '@material-ui/core';
import { Typography } from '@orijinworks/frontend-commons';
import MemberAutocomplete from '../../../../../Feeds/components/AdminLearnerModal/MemberAutocomplete';
import {
  formatEditorLabel,
  isPersonOptionEqual,
  passthroughFilterOptions,
} from '../../../../utils/case-plan-create.util';
import SectionTitle from '../SectionTitle';
import EditorChip from './EditorChip';
import { useEditorsSectionHook } from './useEditorsSectionHook';
import { useEditorsSectionStyles } from './EditorsSection.styles';

/**
 * Optional editors section using shared MemberAutocomplete.
 * Presentational only — logic lives in useEditorsSectionHook.
 */
export const EditorsSection = ({
  editors,
  onEditorsChange,
  showTitle = true,
  includeCreatorDefault = true,
}) => {
  const classes = useEditorsSectionStyles();
  const {
    isLoading,
    selectedMembers,
    memberOptions,
    helperText,
    handleSearchChange,
    handleMembersChange,
    handleRemoveEditor,
    getOptionDisabled,
  } = useEditorsSectionHook(editors, onEditorsChange, { includeCreatorDefault });

  return (
    <Box className={classes.root}>
      {showTitle ? <SectionTitle title="Editors" suffix="- optional" /> : null}
      <Box className={classes.search}>
        <MemberAutocomplete
          multiple
          label="Plan Editors"
          placeholder="Search staff by name or username"
          memberOptions={memberOptions}
          selectedMembers={selectedMembers}
          onMembersChange={handleMembersChange}
          onSearchChange={handleSearchChange}
          isMemberListLoading={isLoading}
          filterOptions={passthroughFilterOptions}
          getOptionLabel={formatEditorLabel}
          getOptionDisabled={getOptionDisabled}
          isOptionEqualToValue={isPersonOptionEqual}
          rootClassName="editors-autocomplete-root"
        />
        <Box className={classes.supportingText}>
          <InfoOutlinedIcon className={classes.infoIcon} fontSize="inherit" />
          <Typography variant="subtitle1" className={classes.helper}>
            {helperText}
          </Typography>
        </Box>
        <Box className={classes.chips}>
          {editors.map((editor) => (
            <EditorChip
              key={editor.personId}
              editor={editor}
              onRemove={handleRemoveEditor}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

EditorsSection.propTypes = {
  editors: PropTypes.arrayOf(PropTypes.object),
  onEditorsChange: PropTypes.func.isRequired,
  showTitle: PropTypes.bool,
  includeCreatorDefault: PropTypes.bool,
};

EditorsSection.defaultProps = {
  editors: [],
  showTitle: true,
  includeCreatorDefault: true,
};

export default EditorsSection;
